/**
 * «Συσκευές ταμείου» (24/10/2026) — οθόνη πελάτη και αναγνώριση κλήσης (modem) ανά υπολογιστή ταμείου, από το
 * πάνελ support / super admin αντί για το .env του PC. Για τεχνικούς χωρίς ειδικές γνώσεις: κάθε συσκευή έχει
 * μία φράση κατάστασης, ένα κουμπί «Βρες…» που κάνει όλη τη δουλειά και μία «Δοκιμή»· τα πεδία θύρας/ταχύτητας
 * μένουν κρυμμένα στη «Χειροκίνητη ρύθμιση».
 *
 * Θέλει agent 2.4.0+ στο PC. Ό,τι δεν αποθηκευτεί από εδώ ισχύει από το .env, όπως πριν. API:
 * backend/routes/cashierDevices.js. Λογική χωρίς React: cashierDevicesModel.ts.
 *
 * 25/10/2026: και από το διαχειριστικό του καταστήματος — σελίδα «Συσκευές ταμείου» στο «Ταμείο»
 * (settings/CashierDevicesPage), με το CashierDevicesPanel μέσα στη σελίδα και το storeDevicesApi.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Monitor, PhoneCall, RefreshCw, X, XCircle } from 'lucide-react';
import { GHOST_BTN, INPUT_CLS, Notice, PRIMARY_BTN, SELECT_CLS, StatusChip } from '../shared/listPage';
import CashierDisplayWizard from './CashierDisplayWizard';
import { DevicesApi, DevicesScope, supportDevicesApi } from './cashierDevicesApi';
import {
  CallerCfg, DISPLAY_SETTINGS, DevicesResponse, DisplayCfg, GREEK_TRIALS, Pc, callerStatus, displayStatus, encodingLabel,
  mergeSave, modeLabel,
} from './cashierDevicesModel';

const errorOf = (e: any) => e?.response?.data?.error || 'Αποτυχία — δοκίμασε ξανά';
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const MODEM_BAUDS = [115200, 57600, 38400, 19200, 9600];
const DISPLAY_BAUDS = [9600, 2400, 19200, 4800];

type ModemResult = { path: string; ok: boolean; model?: string | null; error?: string };

/** Μία συσκευή: οθόνη πελάτη. */
const DisplaySection: React.FC<{
  storeId: number; pc: Pc; storeSwitchOn: boolean; onSaved: () => void; api: DevicesApi; scope: DevicesScope;
}> = ({ storeId, pc, storeSwitchOn, onSaved, api, scope }) => {
  const live = pc.live!;
  const st = displayStatus(live.display);
  const cfg = live.display.config;
  const [wizard, setWizard] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  const [manual, setManual] = useState<DisplayCfg>({
    enabled: true, port: cfg?.port || live.ports[0]?.path || '', baudRate: cfg?.baudRate || 9600, mode: cfg?.mode || 'epson',
    encoding: cfg?.encoding || 'cp737', codePage: cfg?.codePage ?? null, name: cfg?.name || '',
  });
  const uid = pc.uid as string;

  const save = async (display: DisplayCfg | null, okText: string) => {
    setBusy('save'); setMsg(null);
    try {
      await api.saveDevices(storeId, uid, mergeSave(pc, { display }));
      setMsg({ tone: 'ok', text: okText });
      onSaved();
    } catch (e: any) { setMsg({ tone: 'danger', text: errorOf(e) }); }
    finally { setBusy(null); }
  };

  const test = async () => {
    setBusy('test'); setMsg(null);
    try {
      const r: any = await api.deviceCmd(storeId, uid, 'display-test', { lines: ['ΔΟΚΙΜΗ ΟΘΟΝΗΣ', 'ΟΛΑ ΚΑΛΑ'] });
      setMsg(r?.ok
        ? { tone: 'ok', text: 'Στάλθηκε. Η οθόνη πρέπει να δείχνει «ΔΟΚΙΜΗ ΟΘΟΝΗΣ — ΟΛΑ ΚΑΛΑ».' }
        : { tone: 'danger', text: r?.error || 'Η δοκιμή απέτυχε' });
    } catch (e: any) { setMsg({ tone: 'danger', text: errorOf(e) }); }
    finally { setBusy(null); }
  };

  const greekValue = GREEK_TRIALS.find(t => t.encoding === manual.encoding && (t.codePage ?? null) === (manual.codePage ?? null))?.n ?? 1;

  return (
    <section className="space-y-3" aria-label="Οθόνη πελάτη" data-testid={`display-${uid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Monitor className="w-4 h-4 text-t3" aria-hidden="true" />
        <h4 className="font-semibold text-t1">Οθόνη πελάτη</h4>
        <StatusChip tone={st.tone} testId={`display-status-${uid}`}>{st.text}</StatusChip>
      </div>
      {cfg && (
        <p className="text-xs text-t3">
          {cfg.port} · {cfg.baudRate} · {modeLabel(cfg.mode)} · ελληνικά {encodingLabel(cfg.encoding, cfg.codePage)}
          {live.display.source === 'env' ? ' · ρυθμίσεις από το .env του υπολογιστή' : ''}
        </p>
      )}
      {!storeSwitchOn && (
        <Notice testId="display-switch-off">
          {scope === 'store'
            ? <span>Ο διακόπτης «Οθόνη πελάτη στο Take Away» πιο πάνω είναι <b>κλειστός</b>: τα ταμεία δεν θα στέλνουν τίποτα στην οθόνη.</span>
            : <span>Ο διακόπτης «Οθόνη πελάτη στο Take Away» του καταστήματος είναι <b>κλειστός</b>: τα ταμεία δεν θα στέλνουν τίποτα στην οθόνη. Ανοίγει από το διαχειριστικό του καταστήματος (Ταμείο → Συσκευές ταμείου).</span>}
        </Notice>
      )}

      {wizard ? (
        <CashierDisplayWizard
          storeId={storeId}
          pc={pc}
          api={api}
          onCancel={() => setWizard(false)}
          onDone={() => { setWizard(false); setMsg({ tone: 'ok', text: 'Η οθόνη πελάτη ρυθμίστηκε.' }); onSaved(); }}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => { setMsg(null); setWizard(true); }} className={cfg && live.display.state === 'ok' ? GHOST_BTN : PRIMARY_BTN} data-testid={`display-find-${uid}`}>
            Βρες την οθόνη
          </button>
          {cfg && (
            <button type="button" onClick={test} disabled={!!busy} className={GHOST_BTN} data-testid={`display-test-${uid}`}>
              {busy === 'test' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null} Δοκιμή
            </button>
          )}
          {cfg && (
            <button type="button" onClick={() => save({ enabled: false }, 'Η οθόνη πελάτη έκλεισε σε αυτόν τον υπολογιστή.')} disabled={!!busy} className={GHOST_BTN}>
              Δεν έχει οθόνη — κλείσ' την
            </button>
          )}
        </div>
      )}

      {msg && <p className={`text-sm ${msg.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} role="status">{msg.text}</p>}

      {!wizard && (
        <details className="text-sm">
          <summary className="cursor-pointer text-t3 hover:text-t1">Χειροκίνητη ρύθμιση (για προχωρημένους)</summary>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-t2">Θύρα
              <select className={`${SELECT_CLS} mt-1`} value={manual.port} onChange={e => setManual(m => ({ ...m, port: e.target.value }))} data-testid={`display-port-${uid}`}>
                {live.ports.map(p => <option key={p.path} value={p.path}>{p.label}{p.usedBy === 'callerId' ? ' (modem)' : ''}</option>)}
              </select>
            </label>
            <label className="text-t2">Ταχύτητα
              <select className={`${SELECT_CLS} mt-1`} value={manual.baudRate} onChange={e => setManual(m => ({ ...m, baudRate: Number(e.target.value) }))}>
                {DISPLAY_BAUDS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="text-t2">Τύπος
              <select className={`${SELECT_CLS} mt-1`} value={manual.mode} onChange={e => setManual(m => ({ ...m, mode: e.target.value as DisplayCfg['mode'] }))}>
                {Array.from(new Set(DISPLAY_SETTINGS.map(s => s.mode))).map(m => <option key={m} value={m}>{modeLabel(m)}</option>)}
              </select>
            </label>
            <label className="text-t2">Ελληνικά
              <select className={`${SELECT_CLS} mt-1`} value={greekValue} onChange={e => {
                const t = GREEK_TRIALS.find(g => g.n === Number(e.target.value))!;
                setManual(m => ({ ...m, encoding: t.encoding, codePage: t.codePage }));
              }}>
                {GREEK_TRIALS.map(t => <option key={t.n} value={t.n}>{t.n} — {t.hint}</option>)}
              </select>
            </label>
            <label className="text-t2 sm:col-span-2">Όνομα οθόνης
              <input className={`${INPUT_CLS} mt-1`} value={manual.name || ''} maxLength={80} onChange={e => setManual(m => ({ ...m, name: e.target.value }))} />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={PRIMARY_BTN} disabled={!!busy || !manual.port} data-testid={`display-manual-save-${uid}`}
              onClick={() => save({ ...manual, enabled: true, id: live.display.displayId || cfg?.id || null, name: (manual.name || '').trim() || null }, 'Αποθηκεύτηκε.')}>
              Αποθήκευση
            </button>
            {pc.saved?.display && (
              <button type="button" className={GHOST_BTN} disabled={!!busy} onClick={() => save(null, 'Η οθόνη ακολουθεί πάλι το .env του υπολογιστή.')}>
                Επιστροφή στο .env
              </button>
            )}
          </div>
        </details>
      )}
    </section>
  );
};

/** Μία συσκευή: αναγνώριση κλήσης (modem). */
const CallerSection: React.FC<{ storeId: number; pc: Pc; onSaved: () => void; api: DevicesApi }> = ({ storeId, pc, onSaved, api }) => {
  const live = pc.live!;
  const st = callerStatus(live.callerId);
  const cfg = live.callerId.config;
  const uid = pc.uid as string;
  const [busy, setBusy] = useState<string | null>(null);
  const [scan, setScan] = useState<ModemResult[] | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  const [call, setCall] = useState<{ phase: 'waiting' | 'ok' | 'none'; left?: number; end?: string | null } | null>(null);
  const [manual, setManual] = useState<CallerCfg>({ enabled: true, port: cfg?.port || 'COM5', baudRate: cfg?.baudRate || 115200 });
  const cancelCall = useRef(false);
  useEffect(() => () => { cancelCall.current = true; }, []);

  const save = async (callerId: CallerCfg | null, okText: string) => {
    setBusy('save'); setMsg(null);
    try {
      await api.saveDevices(storeId, uid, mergeSave(pc, { callerId }));
      setScan(null);
      setMsg({ tone: 'ok', text: okText });
      onSaved();
    } catch (e: any) { setMsg({ tone: 'danger', text: errorOf(e) }); }
    finally { setBusy(null); }
  };

  const find = async () => {
    setBusy('scan'); setMsg(null); setScan(null); setCall(null);
    try {
      const r: any = await api.deviceCmd(storeId, uid, 'modem-scan');
      if (r?.ok) setScan(r.results || []);
      else setMsg({ tone: 'danger', text: r?.error || 'Η αναζήτηση απέτυχε' });
    } catch (e: any) { setMsg({ tone: 'danger', text: errorOf(e) }); }
    finally { setBusy(null); }
  };

  const endScan = async () => {
    setScan(null);
    await api.deviceCmd(storeId, uid, 'resume').catch(() => {});
    onSaved();
  };

  const testCall = async () => {
    cancelCall.current = false;
    setMsg(null);
    const since = new Date(Date.now() - 2000).toISOString();
    for (let left = 60; left > 0 && !cancelCall.current; left -= 2) {
      setCall({ phase: 'waiting', left });
      try {
        const r: any = await api.getLastModemCall(storeId, since);
        if (r?.call) { setCall({ phase: 'ok', end: r.call.phoneEnd }); return; }
      } catch (e) { /* ξανά στο επόμενο */ }
      await sleep(2000);
    }
    if (!cancelCall.current) setCall({ phase: 'none' });
  };

  const found = (scan || []).filter(r => r.ok);

  return (
    <section className="space-y-3" aria-label="Αναγνώριση κλήσης" data-testid={`caller-${uid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <PhoneCall className="w-4 h-4 text-t3" aria-hidden="true" />
        <h4 className="font-semibold text-t1">Αναγνώριση κλήσης</h4>
        <StatusChip tone={st.tone} testId={`caller-status-${uid}`}>{st.text}</StatusChip>
      </div>
      {cfg && live.callerId.source === 'env' && (
        <p className="text-xs text-t3">Ρυθμίσεις από το .env του υπολογιστή ({cfg.port} · {cfg.baudRate}). Χωρίς ρύθμιση ξεκινά πάντα στη COM5.</p>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={find} disabled={!!busy} className={cfg && live.callerId.state === 'ok' ? GHOST_BTN : PRIMARY_BTN} data-testid={`caller-find-${uid}`}>
          {busy === 'scan' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null} Βρες το modem
        </button>
        {cfg && live.callerId.state === 'ok' && (
          <button type="button" onClick={testCall} disabled={!!busy || call?.phase === 'waiting'} className={GHOST_BTN} data-testid={`caller-test-${uid}`}>
            Δοκιμή κλήσης
          </button>
        )}
        {cfg && (
          <button type="button" onClick={() => save({ enabled: false }, 'Η αναγνώριση κλήσης έκλεισε σε αυτόν τον υπολογιστή.')} disabled={!!busy} className={GHOST_BTN}>
            Δεν έχει modem — κλείσ' την
          </button>
        )}
      </div>
      {busy === 'scan' && <p className="text-sm text-t3" aria-live="polite">Ψάχνω modem σε όλες τις θύρες του υπολογιστή — περίμενε έως 30 δευτερόλεπτα…</p>}

      {scan && (
        <div className="rounded-xl border border-line p-3 space-y-2" data-testid={`caller-scan-${uid}`}>
          <ul className="text-sm space-y-1">
            {scan.map(r => (
              <li key={r.path} className="flex items-center gap-2">
                {r.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-hidden="true" /> : <XCircle className="w-4 h-4 text-t4" aria-hidden="true" />}
                <span className={r.ok ? 'text-t1 font-medium' : 'text-t3'}>
                  {r.path} — {r.ok ? `modem${r.model ? ` (${r.model})` : ''}` : r.error ? `δεν ανοίγει (${r.error})` : 'δεν απάντησε'}
                </span>
              </li>
            ))}
            {scan.length === 0 && <li className="text-t3">Ο υπολογιστής δεν έχει σειριακές θύρες.</li>}
          </ul>
          {found.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {found.map(r => (
                <button key={r.path} type="button" className={PRIMARY_BTN} disabled={!!busy} data-testid={`caller-use-${r.path}`}
                  onClick={() => save({ enabled: true, port: r.path, baudRate: 115200 }, `Η αναγνώριση κλήσης δουλεύει πλέον με το modem στη ${r.path}.`)}>
                  Χρησιμοποίησε το modem στη {r.path}
                </button>
              ))}
              <button type="button" className={GHOST_BTN} onClick={endScan}>Άκυρο</button>
            </div>
          ) : (
            <>
              <p className="text-sm text-t2">Δεν βρέθηκε modem. Έλεγξε ότι είναι συνδεδεμένο και αναμμένο. Αν αυτό το ταμείο <b>δεν έχει</b> modem, κλείσε την αναγνώριση κλήσης, ώστε να μην πιάνει άδικα τη θύρα.</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" className={PRIMARY_BTN} disabled={!!busy} onClick={() => save({ enabled: false }, 'Η αναγνώριση κλήσης έκλεισε σε αυτόν τον υπολογιστή.')}>
                  Δεν έχει modem — κλείσ' την
                </button>
                <button type="button" className={GHOST_BTN} onClick={endScan}>Άκυρο</button>
              </div>
            </>
          )}
        </div>
      )}

      {call?.phase === 'waiting' && (
        <Notice testId={`caller-call-wait-${uid}`}>
          <span>Πάρε τηλέφωνο στο κατάστημα <b>τώρα</b>, από ένα κινητό. Περιμένω την κλήση… ({call.left}″)</span>
        </Notice>
      )}
      {call?.phase === 'ok' && <p className="text-sm text-emerald-700 dark:text-emerald-400" role="status">Ήρθε η κλήση{call.end ? ` (…${call.end})` : ''}. Η αναγνώριση κλήσης δουλεύει.</p>}
      {call?.phase === 'none' && (
        <Notice tone="danger" testId={`caller-call-none-${uid}`}>
          Δεν ήρθε κλήση. Έλεγξε ότι η γραμμή του τηλεφώνου μπαίνει στην υποδοχή «LINE» του modem και ότι το τηλέφωνο χτύπησε τουλάχιστον δύο φορές.
        </Notice>
      )}
      {msg && <p className={`text-sm ${msg.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} role="status">{msg.text}</p>}

      <details className="text-sm">
        <summary className="cursor-pointer text-t3 hover:text-t1">Χειροκίνητη ρύθμιση (για προχωρημένους)</summary>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-t2">Θύρα
            <select className={`${SELECT_CLS} mt-1`} value={manual.port} onChange={e => setManual(m => ({ ...m, port: e.target.value }))}>
              {Array.from(new Set([manual.port || '', ...live.ports.map(p => p.path)])).filter(Boolean).map(p => <option key={p} value={p}>{live.ports.find(x => x.path === p)?.label || p}</option>)}
            </select>
          </label>
          <label className="text-t2">Ταχύτητα
            <select className={`${SELECT_CLS} mt-1`} value={manual.baudRate} onChange={e => setManual(m => ({ ...m, baudRate: Number(e.target.value) }))}>
              {MODEM_BAUDS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className={PRIMARY_BTN} disabled={!!busy || !manual.port} onClick={() => save({ ...manual, enabled: true }, 'Αποθηκεύτηκε.')}>Αποθήκευση</button>
          {pc.saved?.callerId && (
            <button type="button" className={GHOST_BTN} disabled={!!busy} onClick={() => save(null, 'Η αναγνώριση κλήσης ακολουθεί πάλι το .env του υπολογιστή.')}>
              Επιστροφή στο .env
            </button>
          )}
        </div>
      </details>
    </section>
  );
};

const PcCard: React.FC<{
  storeId: number; pc: Pc; storeSwitchOn: boolean; onReload: () => void; api: DevicesApi; scope: DevicesScope; stacked: boolean;
}> = ({ storeId, pc, storeSwitchOn, onReload, api, scope, stacked }) => {
  // Μετά από αποθήκευση: ο agent ανοίγει τις συσκευές σε 1–2″, μετά ξαναδιαβάζουμε την κατάσταση.
  const reloadSoon = useCallback(() => { setTimeout(onReload, 1500); }, [onReload]);
  return (
    <article className="rounded-2xl border border-line bg-surface-2 p-4 space-y-4" data-testid={`pc-${pc.uid || pc.machine}`}>
      <header>
        <h3 className="font-bold text-t1">{pc.machine || 'Υπολογιστής ταμείου'}</h3>
        <p className="text-xs text-t3">
          agent {pc.version || 'παλιός'}{pc.printers.length ? ` · εκτυπωτές: ${pc.printers.join(', ')}` : ''}
        </p>
      </header>
      {!pc.supported ? (
        <Notice testId="pc-old-agent">
          <span>
            Αυτός ο υπολογιστής έχει agent <b>{pc.version || 'παλιό'}</b>. Για να ρυθμίσεις οθόνη και modem από εδώ χρειάζεται <b>2.4.0</b> ή νεότερος
            {scope === 'store' ? ' — ζήτησε την αναβάθμιση από την υποστήριξη Xentrix' : ': Super admin → Εκδόσεις agent'}. Ως τότε ισχύει το .env του υπολογιστή.
          </span>
        </Notice>
      ) : pc.duplicate ? (
        <Notice tone="danger" testId="pc-duplicate">
          Δύο υπολογιστές έχουν το ίδιο αναγνωριστικό agent (αντιγράφηκε ο φάκελος του agent). Σβήσε το αρχείο agent-id.json στον έναν και κάνε επανεκκίνηση της υπηρεσίας.
        </Notice>
      ) : !pc.live ? (
        <Notice tone="danger" testId="pc-no-answer">
          <span>Ο υπολογιστής δεν απάντησε{pc.liveError ? `: ${pc.liveError}` : ''}. </span>
          <button type="button" className="underline" onClick={onReload}>Ξαναδοκίμασε</button>
        </Notice>
      ) : (
        <div className={`grid grid-cols-1 gap-6 ${stacked ? '' : 'lg:grid-cols-2'}`}>
          <DisplaySection storeId={storeId} pc={pc} storeSwitchOn={storeSwitchOn} onSaved={reloadSoon} api={api} scope={scope} />
          <CallerSection storeId={storeId} pc={pc} onSaved={reloadSoon} api={api} />
        </div>
      )}
    </article>
  );
};

/**
 * Τα PC του καταστήματος, χωρίς πλαίσιο — μέσα στο παράθυρο του support (CashierDevices) ή μέσα στη σελίδα
 * «Συσκευές ταμείου» του διαχειριστικού. storeSwitchOn: ο διακόπτης όπως τον ξέρει η σελίδα (π.χ. μόλις
 * αποθηκεύτηκε)· χωρίς αυτόν, όπως τον έστειλε ο server. stacked: οθόνη και modem το ένα κάτω από το άλλο
 * (στενή στήλη της σελίδας ρυθμίσεων)· αλλιώς δίπλα-δίπλα σε φαρδιά οθόνη.
 */
export const CashierDevicesPanel: React.FC<{
  storeId: number; api?: DevicesApi; scope?: DevicesScope; storeSwitchOn?: boolean; stacked?: boolean;
}> = ({ storeId, api = supportDevicesApi, scope = 'support', storeSwitchOn, stacked = false }) => {
  const [data, setData] = useState<DevicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.getDevices(storeId) as DevicesResponse);
      setError(null);
    } catch (e: any) { setError(errorOf(e)); }
    finally { setLoading(false); }
  }, [storeId, api]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="cashier-devices-panel">
      <div className="flex justify-end">
        <button type="button" onClick={load} className={GHOST_BTN} data-testid="devices-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> Ανανέωση
        </button>
      </div>
      {error && <Notice tone="danger" testId="devices-error">{error}</Notice>}
      {loading && !data && (
        <p className="flex items-center gap-2 text-t3"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Ρωτάω τους υπολογιστές του καταστήματος…</p>
      )}
      {data && !data.tableReady && (
        <Notice tone="danger" testId="devices-no-table">Λείπει το migration 279 σε αυτόν τον server: η κατάσταση φαίνεται, αλλά η αποθήκευση δεν θα περάσει.</Notice>
      )}
      {data && data.pcs.length === 0 && (
        <Notice testId="devices-none">
          <span>Κανένας υπολογιστής ταμείου δεν είναι συνδεδεμένος τώρα. Η υπηρεσία «Xentrix Print Agent» πρέπει να τρέχει στο PC του ταμείου.</span>
        </Notice>
      )}
      {data && data.pcs.map((pc, i) => (
        <PcCard key={pc.uid || `${pc.machine}-${i}`} storeId={storeId} pc={pc} api={api} scope={scope} stacked={stacked}
          storeSwitchOn={storeSwitchOn ?? data.customerDisplayEnabled} onReload={load} />
      ))}
    </div>
  );
};

/** Το παράθυρο του πάνελ support / super admin. */
const CashierDevices: React.FC<{ storeId: number; storeName: string; onClose: () => void }> = ({ storeId, storeName, onClose }) => {
  const uid = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="cashier-devices">
      <div role="dialog" aria-modal="true" aria-labelledby={`${uid}-title`}
        className="bg-surface-1 border border-line rounded-2xl w-full max-w-4xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-line shrink-0">
          <div className="min-w-0">
            <h2 id={`${uid}-title`} className="text-lg font-bold text-t1">Συσκευές ταμείου</h2>
            <p className="text-xs text-t3 truncate">{storeName} · οθόνη πελάτη και αναγνώριση κλήσης ανά υπολογιστή</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-t3 hover:text-t1" aria-label="Κλείσιμο"><X className="w-5 h-5" aria-hidden="true" /></button>
        </div>
        <div className="p-6 overflow-y-auto">
          <CashierDevicesPanel storeId={storeId} />
        </div>
      </div>
    </div>
  );
};

export default CashierDevices;
